import { Box } from '@mui/material';
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { GetStaticPropsContext, InferGetStaticPropsType, NextPage } from 'next';
import Image from 'next/image';

import { isProd } from 'utils/env.utils';

import { Duration } from 'constants/duration.constants';
import { useLocale } from 'hooks/useLocale';
import { seoTitleSuffixQuery } from 'queries/global/queries';
import Prefetcher from 'services/prefetcher';

import AppCompactNavigation from 'components/@navigation/AppCompactNavigation';
import SaudiRewardsRetroRedeemClosedContainer from 'components/@saudi-rewards/SaudiRewardsRetroRedeemClosedContainer';
import SaudiRewardsRetroRedeemContainer from 'components/@saudi-rewards/SaudiRewardsRetroRedeemContainer';
import BasicSeo from 'components/@seo/BasicSeo';

const RETRO_EARN_TZ = 'Asia/Riyadh';

const today = toZonedTime(new Date(), RETRO_EARN_TZ);
const start = startOfDay(new Date(2025, 1, 28));
const end = endOfDay(new Date(2025, 3, 15));

const isRetroEarnCampaignActive = isProd
  ? isWithinInterval(today, { start, end })
  : true;

const SaudiRewardsRetroRedeem: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ seoTitleSuffix }) => {
  const { locale } = useLocale();

  return (
    <AppCompactNavigation
      secondaryImage={
        <Box
          sx={{
            height: 24,
            minWidth: 59,
            borderRadius: 1,
            position: 'relative',
          }}
        >
          <Image
            src={`/images/saudi-rewards/brand-logo-saudi-rewards-${locale}.webp`}
            fill
            alt="Saudi Rewards logo"
            style={{
              objectFit: 'contain',
              padding: '0 2px',
              filter: 'brightness(0) invert(1)',
            }}
            placeholder="empty"
            unoptimized
          />
        </Box>
      }
    >
      <BasicSeo
        titleIntlId="seo.saudi_rewards.earn.claim.title"
        titleSuffix={seoTitleSuffix}
      />
      {isRetroEarnCampaignActive ? (
        <SaudiRewardsRetroRedeemContainer />
      ) : (
        <SaudiRewardsRetroRedeemClosedContainer />
      )}
    </AppCompactNavigation>
  );
};

export default SaudiRewardsRetroRedeem;

export const getStaticProps = async ({
  locale,
  preview,
}: GetStaticPropsContext) => {
  const prefetcher = await Prefetcher.init({ locale, preview });
  const { seoTitleSuffix } = await prefetcher.prefetch(seoTitleSuffixQuery);

  return {
    props: {
      seoTitleSuffix,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
